import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { createPortal } from "react-dom";
import styled from "styled-components";
import {
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  EyeOff,
  HelpCircle,
  Menu,
  ChevronDown,
  ChevronRight,
  Plus,
  RotateCcw,
  X,
} from "react-feather";
import {
  AnimateHeight,
  AnimateHeightRef,
  Banner,
  Button,
  Section,
  Spinner,
} from "@cinesys/ui/components";
import SpotterHeader from "../SpotterHeader";
import SpotterMark from "../SpotterMark";
import { SPOTTER_STRINGS } from "../strings";
import { isSpotterAccessDeniedError } from "../accessError";
import { SPOTTER_INTERFACE_ID } from "../config";
import { useInterfaceAccess } from "../../../hooks/useInterfaceAccess";
import InterfaceAccessNotice from "../../../components/InterfaceAccessNotice";
import { DelayedSpinner } from "@cinesys/experience-ui/components";
import AnchorReviewPanel from "../filmsGround/AnchorReviewPanel";
import NewRunModal from "./NewRunModal";
import { PAGE_WRAP_VERTICAL_PADDING_PX } from "../pageLayout";
import { AnchorJobStatus, useAnchorJobs } from "../filmsGround/service";
import {
  ControlRoomJob,
  SpotterJobMainStage,
  useControlRoomJobs,
  useSetControlRoomJobDismissed,
} from "./service";
import { resolveRowLabel } from "./rowLabel";
import {
  hideButtonMode,
  primaryJobState,
  STALE_JOB_HIDE_THRESHOLD_MS,
  tabForJob,
} from "./jobBucketing";
import type { ControlRoomTabKey, HideButtonMode } from "./jobBucketing";

// Re-exported so this screen's own tests (`tabForJob.unit.test.ts`, `hideButtonMode.unit.test.ts`)
// keep importing from here unchanged — the implementations moved to `jobBucketing.ts` so
// `SpotterHeader.tsx`'s Control Room tab badge could reuse them without a `ControlRoomScreen.tsx`
// <-> `SpotterHeader.tsx` import cycle (see that file's own doc comment).
export { hideButtonMode, STALE_JOB_HIDE_THRESHOLD_MS, tabForJob };
export type { ControlRoomTabKey, HideButtonMode };

/**
 * Spotter Control Room tab — the customer-facing read side of the status-push pipeline: every job
 * Spotter is running for this org, and how far along each one is. Built entirely on this app's own
 * established Spotter patterns (`SpotterHeader` + `Page`/`Wrap`, `Banner`/`Spinner` for load/error
 * states, `--spotter*` theme tokens).
 *
 * ONE-PAGE RAIL: a single route. The grouped job rail (`RAIL_SECTION_ORDER` below) is both the
 * navigation and the filter — clicking a rail row opens `AnchorReviewPanel` for that job inline,
 * in the same two-column layout, at the same route (`spotter/control-room/:jobId`). `:jobId`
 * absent shows a real "nothing selected" state. A row's per-job review status badge (Awaiting
 * start / Analyzing / Ready to confirm / Submitted) is a genuinely separate signal from the
 * generic stage pill `stageDisplay` renders (see that function's doc comment); `RailRowContent`
 * fetches it itself (`useAnchorJobs()`, keyed by `jobId`) and renders it as `ReviewStatusBadge` in
 * place of the generic pill for any row with an open review — see that component's doc comment.
 *
 * SUBMIT BLOCK: `AnchorConfirmContent.tsx`'s "N of M"/progress bar/Submit button renders inline,
 * in its own sticky section under its own heading — this screen threads no header portal target
 * through `AnchorReviewPanel`. `SpotterHeader`'s `headerExtra` prop is unrelated to this screen;
 * `FieldsScreen.tsx` still uses it for its own header progress meter.
 *
 * REFRESH: polls every `CONTROL_ROOM_POLL_INTERVAL_MS` (`service.ts`) — n8n's pushes are
 * fire-and-forget with nothing that could push a live update INTO this app, so polling is the
 * only option; a run here takes minutes not seconds, so 5s (this app's existing "watch a
 * background job" interval, see `service.ts`'s doc comment) is real-time enough without
 * hammering the API.
 *
 * SCOPE: one list, org-wide — `GET /spotter/control-room` is the only thing the store can key on
 * (one row per `job_id`, scoped to the org's own `systemDomainId` pk; there is no
 * collection/project grouping anywhere in the pushed payload to list by instead, see the
 * customer's spec). Newest-updated jobs sort first (server-side), so an active run naturally
 * surfaces at the top without any client-side sort/filter UI for a first pass.
 *
 * STAGE DISPLAY: `needs_input`/`error` are side-states layered on top of the five ranked main
 * stages, not stages of their own (see `spotterJobStatusStore.ts`'s doc comment) — `stageDisplay`
 * below picks ONE thing to show per job. An open anchor-job review (`hasReview &&
 * reviewNeedsInput`) wins over EVERYTHING, including `done` — see `primaryJobState`'s own doc
 * comment for why a job's own pipeline finishing must never bury a still-unconfirmed Films
 * Ground/Top Films review. Below that, `done` wins (terminal — done never regresses), then `error`
 * (the more actionable signal — something broke), then the plain current stage. A plain
 * `needsInput` latch with no review (`state === "needsInput"` in `primaryJobState`, as opposed to
 * `"needsReview"`) does NOT get its own pill — `tabForJob`'s bucketing is untouched by this, so a
 * plain-latch job still lands under "Needs you", it just doesn't say so on its own pill. The
 * stepper dots underneath always reflect real stage progress (`stageRank`) regardless of which of
 * those wins the headline pill.
 *
 * `counts` is deliberately absent everywhere in this screen and its `service.ts` types — it stays
 * stage-name-only with no numbers, and the backend never sends it.
 *
 * RAIL SECTIONS AND BUCKETING: `primaryJobState`/`tabForJob` below derive each section from the
 * SAME real fields `stageDisplay` already keys off (`hasReview`/`reviewNeedsInput`/`latestStage`/
 * `latestStatus`/`needsInput`), not an invented parallel model — an open anchor-job review wins
 * first, THEN `done`, then an `error` status, then the `needsInput` latch, else the job is still
 * moving through its ranked stages — `hidden` wins over all of it, checked before `primaryJobState`
 * even runs (a hidden job lands under Hidden and nowhere else). Needs You starts expanded, the
 * other three start folded (`DEFAULT_RAIL_FOLD`), so at rest the rail shows only open work while
 * nothing is hidden from view. Fold state persists per browser (`localStorage`, same convention
 * `GamebookScreen.tsx`'s season/week picker already uses) — NOT scoped to a user identity, since
 * this app's session model carries none (only `systemDomainId`, org-wide, same reason `dismissed`
 * itself is org-wide — see `useSetControlRoomJobDismissed`'s doc comment). An inbound
 * `?tab=<section>` link expands that section on load instead of doing nothing — see the
 * `foldState` initializer below.
 *
 * STALE: a job also lands in Hidden once `lastUpdatedAt` is older than
 * `STALE_JOB_HIDE_THRESHOLD_MS`, alongside the manual `dismissed` path below — hidden outright
 * after 24h, no separate "expired" label, no per-state exemptions. Derived, not persisted — a job
 * that resumes updating rejoins its normal section on its own.
 *
 * HIDE: the feature's user-facing name is "Hide"/"Hidden"; the underlying field is `job.dismissed`.
 * `useSetControlRoomJobDismissed` (`service.ts`) persists it server-side on the job's own status
 * row (`PUT /spotter/control-room/:jobId/dismiss`) — NOT client-only `useState`, so it survives a
 * reload. The hide control itself is a round, icon-only `Button` with no `label`/`size`/`iconSize`
 * override; its accessible name comes entirely from `tooltip`, which `Button`'s own `Tooltip`
 * wrapper turns into a real `aria-label`.
 *
 * UNHIDE (Monday 12952714235, Addison: "should just restart timer"): an explicit unhide doesn't
 * exempt a job from STALE above, it restarts the clock — `job.unhiddenAt` feeds `isStale`'s own
 * baseline alongside `lastUpdatedAt`, so an unhidden job goes stale (and re-hides) on its own after
 * another `STALE_JOB_HIDE_THRESHOLD_MS` of silence, same as any other job. No permanent exemption
 * class. The row's hide/unhide control (`hideButtonMode`, below `tabForJob`) reads whether the row
 * is ACTUALLY under Hidden, not `job.dismissed` in isolation, so a stale-only row still offers
 * Unhide.
 *
 * SAFARI STICKY RISK: `Page`'s `flex-shrink: 0` (below) is the same fix Gamebook's own `Page`
 * needed — without it, `Page` can collapse to its nearest scrolling ancestor's viewport-height
 * ceiling instead of growing to fit its own content in Safari, detaching any `position: sticky`
 * descendant that's stuck past where the collapsed box would have ended. This screen has a SECOND
 * sticky element beyond `SpotterHeader` itself: `RailPane` (sticky, same `stickyTop` convention as
 * `FieldsScreen.tsx`'s own `Aside`), so the rail stays reachable while a long job list scrolls in
 * the review pane.
 *
 * `RailHeader` (the "+ New run" row above "Needs you") USED to be a second, sticky element nested
 * inside `RailPane`'s own `overflow-y: auto` box — a real Safari sticky-in-scroll-container risk
 * (traced structurally, never live-verified: Playwright's WebKit needs GTK4, unavailable in this
 * environment). Addison: "I don't truly want it to be a sticky header, I would rather it appear
 * above the scroll container" — `RailHeader` is now a plain, non-sticky `flex: none` child of
 * `RailColumn`, rendered ABOVE `RailPane` rather than inside it; `RailColumn` itself carries the
 * ONE sticky position for the whole rail block (header + scrollable job list together), so there's
 * only ever one sticky element here, not two nested ones.
 *
 * `RailColumn`'s `height` (a fixed budget, not a cap — Monday 12957358700 follow-up, "rail + full
 * page view are same height") subtracts `PAGE_WRAP_VERTICAL_PADDING_PX` (`../pageLayout.ts`)
 * alongside `--spotterHeaderHeight` — it sits inside `Wrap`, whose own vertical padding is real
 * space the box's bottom edge has to account for too, or it runs past the viewport. Same constant
 * `AnchorConfirmContent.tsx`'s `StageWrap` uses for the identical correction. `RailPane` below it
 * just fills whatever's left (`flex: 1; overflow-y: auto`) beneath the fixed-size header.
 */

/** `flex-shrink: 0` — see the file doc comment's "SAFARI STICKY RISK" section. */
const Page = styled.div`
  min-height: 100%;
  flex-shrink: 0;
  color: var(--spotterInk);
  font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
  font-size: 15px;
  line-height: 1.5;
`;

/** Wider than `GamebookScreen.tsx`'s own page `Wrap` (1320px, coincidentally — separate
 *  styled-components, not shared code): the anchor editor's gamebook band needs real room for
 *  play descriptions; `GamebookScreen.tsx` doesn't have that problem, so it stays narrower. Named
 *  constant so reverting the divergence is one line. */
const PAGE_MAX_WIDTH = "1700px";

const Wrap = styled.div`
  padding: 10px 22px;
  max-width: ${PAGE_MAX_WIDTH};
  margin: 0 auto;

  /* Re-alias @cinesys/ui's generic tokens to this interface's palette (customer-frontend
   * CLAUDE.md convention). */
  --dark400: var(--spotterRaised);
  --dark500: var(--spotterLine2);
  --dark600: var(--spotterLine2);
  --dark700: var(--spotterInkFaint);
  --gray400: var(--spotterInk);
  --gray500: var(--spotterInk);
`;

/** Heading style for the right pane's centered "nothing selected" cards. */
const CardHeading = styled.h1`
  font-size: 19px;
  font-weight: 600;
  letter-spacing: -0.02em;
  margin: 0 0 6px;
`;

/** "+ New run" action + its background-refresh spinner (Point 12) — full width, used only where
 *  there's no rail to pin a header to: the mobile drawer trigger row (`RailPane` itself is
 *  `display: none` there) and standalone above `EmptyPanel` in the zero-jobs-ever state
 *  (`Layout`/`RailPane` don't mount at all in `pageBodyState`'s `"emptyNoJobs"`). The desktop
 *  rail's own "+ New run" lives in `RailHeader`/`NewRunHeader` below instead. `RefreshSlot` is a
 *  fixed-size reserved box (not conditionally rendered itself) so the button never shifts width
 *  when a poll starts/stops next to it. */
const NewRunRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
`;

const NewRunButton = styled(Button)`
  flex: 1;
`;

/** `$size` lets `NewRunHeader` reuse this at a slightly larger size than the row spinner, without
 *  a second near-duplicate styled-component. */
const RefreshSlot = styled.div<{ $size?: number }>`
  flex: none;
  width: ${(p) => p.$size ?? 16}px;
  height: ${(p) => p.$size ?? 16}px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

/** Scoped to the rail header only — the mobile/empty-state row spinner stays at 16px. */
const HEADER_SPINNER_SIZE = 20;

/** The "+ New run" row — a plain, non-sticky `flex: none` child of `RailColumn`, rendered ABOVE
 *  `RailPane` (not inside its `overflow-y: auto` box) so it stays visible above the scrolling job
 *  list without being sticky itself — see the file doc comment. No `border-bottom` (Addison: "get
 *  rid of the border bottom on it") and minimal top padding (Addison: "a bit too much padding
 *  above it"). */
const RailHeader = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 0 2px 10px;
`;

const EmptyPanel = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: var(--spotterInkFaint);
  font-size: 14px;
  background: var(--spotterSurface);
  border: 1px solid var(--spotterLine);
  border-radius: 14px;
`;

/** Rail width fixed rather than resizable — the plan's own note is that 12rem is too narrow for
 *  the row-label content (`RowLabel`'s two lines can run to a full collection + parent name), not
 *  that it needs to flex; a bit over 21rem gives that room without eating into the review pane. */
const RAIL_BREAKPOINT = "1100px";
/** The exact width `RailPane`'s own `@media (min-width: ...)` switches back on at, below — kept
 *  as a real number (not parsed from `RAIL_BREAKPOINT` above) so the mobile-drawer auto-close
 *  effect's `matchMedia` query lines up with the CSS breakpoint precisely, not by coincidence. */
const RAIL_BREAKPOINT_MIN_PX = 1101;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 340px 1fr;
  gap: 20px;
  align-items: start;

  @media (max-width: ${RAIL_BREAKPOINT}) {
    grid-template-columns: 1fr;
  }
`;

/** Sticky, self-scrolling nav column (same `stickyTop`/`overflow-y` shape `FieldsScreen.tsx`'s own
 *  `Aside` uses) — stays reachable while a long anchor-card list scrolls in the review pane next
 *  to it. Becomes a drawer below `RAIL_BREAKPOINT` (`RailDrawerTrigger`/`RailDrawer` below) rather
 *  than squeezing the review pane narrower than it can use.
 *
 *  A real `height`, not `max-height` (Monday 12957358700 follow-up, Addison: "rail + full page
 *  view are same height") — a short rail used to size itself down to its own content, leaving the
 *  review pane (`AnchorConfirmContent.tsx`'s `StageWrap`, always the FULL `100vh - header - padding`
 *  budget) visibly taller than it on any job with only a few rows. `height` forces the same budget
 *  onto both regardless of content — the grid child sized to it is now `RailColumn` (see the file
 *  doc comment), with `RailHeader` (flex: none) and `RailPane` (flex: 1, own `overflow-y: auto`)
 *  splitting that fixed budget between them, rather than `RailPane` alone owning it. Subtracts
 *  `PAGE_WRAP_VERTICAL_PADDING_PX` alongside `--spotterHeaderHeight` for the same reason
 *  `StageWrap` does — this column sits inside `Wrap`, whose own padding is real vertical space its
 *  bottom edge has to account for too, or it runs past the bottom of the viewport. Same constant,
 *  same correction — see `../pageLayout.ts`. */
const RailColumn = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;

  @media (min-width: 1101px) {
    position: sticky;
    top: var(--spotterHeaderHeight, 118px);
    height: calc(
      100vh - var(--spotterHeaderHeight, 118px) -
        ${PAGE_WRAP_VERTICAL_PADDING_PX}px
    );
  }

  @media (max-width: ${RAIL_BREAKPOINT}) {
    display: none;
  }
`;

/** The scrollable job list beneath `RailHeader` — just the "fill whatever's left of `RailColumn`'s
 *  fixed budget, and self-scroll if the list is long enough to need it" half; the sticky
 *  positioning and height budget both live on `RailColumn` now (see its doc comment). */
const RailPane = styled.nav`
  min-width: 0;
  min-height: 0;

  @media (min-width: 1101px) {
    flex: 1;
    overflow-y: auto;
    padding-bottom: 4px;
  }
`;

const RailDrawerTriggerWrap = styled.div`
  display: none;
  margin-bottom: 14px;

  @media (max-width: ${RAIL_BREAKPOINT}) {
    display: block;
  }
`;

const RailDrawerOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: stretch;
  justify-content: flex-start;
`;

const RailDrawerPanel = styled.div`
  width: 100%;
  max-width: 360px;
  height: 100%;
  overflow-y: auto;
  background: var(--spotterPaper);
  border-right: 1px solid var(--spotterLine);
  padding: 16px;

  --dark400: var(--spotterRaised);
  --dark500: var(--spotterLine2);
  --dark600: var(--spotterLine2);
  --dark700: var(--spotterInkFaint);
  --gray400: var(--spotterInk);
  --gray500: var(--spotterInk);
`;

const RailDrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const RailDrawerTitle = styled.h3`
  margin: 0;
  font-size: 15px;
  color: var(--spotterInk);
`;

const RailSectionBlock = styled.div`
  margin-bottom: 2px;
`;

const RailSectionHeaderBtn = styled.button`
  all: unset;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 7px;
  width: 100%;
  padding: 9px 6px;
  cursor: pointer;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-weight: 700;
  color: var(--spotterInkFaint);
  font-family: ui-monospace, Menlo, monospace;

  &:hover,
  &:focus-visible {
    color: var(--spotterInk);
  }
`;

const RailSectionCount = styled.span`
  margin-left: auto;
  font-variant-numeric: tabular-nums;
  opacity: 0.85;
`;

const RailSectionRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 2px 0 10px;
`;

/** A clickable row renders as a real react-router `Link` (`as={Link} to={...}`, see
 *  `RailRowContent` below) rather than a `div` with a hand-rolled `role="button"`/`tabIndex`/
 *  `onKeyDown` — the old jobs `Table` this rail replaced got its own keyboard affordance from
 *  being a real `<a>` under the hood too (round-4 review finding: this row had none at all,
 *  mouse-only). The text/color resets below undo the `<a>`'s default link styling — this still
 *  needs to look like the plain div it used to be, not a blue underlined link. An unclickable row
 *  (no review to open) stays a plain `div`, same as before. */
const RailRow = styled.div<{ $active: boolean; $clickable: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 34px 10px 12px;
  border-radius: 10px;
  color: inherit;
  text-decoration: none;
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};
  background: ${(p) =>
    p.$active ? "var(--spotterAccentSoft)" : "transparent"};
  border: 1px solid
    ${(p) => (p.$active ? "var(--spotterAccent)" : "transparent")};

  ${(p) =>
    p.$clickable &&
    `
    &:hover {
      background: var(--spotterRaised);
    }

    &:focus-visible {
      outline: 2px solid var(--spotterAccent);
      outline-offset: 1px;
    }
  `}
`;

const RailRowAction = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;
`;

const RailRowMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const JobCell = styled.div`
  min-width: 0;
`;

/** The rail row's primary label (Monday 12937644849) — a resolved name/game-identity string,
 *  monospace only when it's genuinely the raw job id (`RowLabel.isJobId`, see `rowLabel.ts`). */
const RowPrimary = styled.div<{ $mono: boolean }>`
  font-family: ${({ $mono }) =>
    $mono ? "ui-monospace, Menlo, monospace" : "inherit"};
  font-size: 12.5px;
  font-weight: 600;
  color: var(--spotterInk);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** The subline under `RowPrimary` — the parent collection, "Saved Search", or a game row's
 *  second line. Only ever rendered when there's real text for it (`RowLabel.secondary`), never as
 *  an empty line. */
const RowSecondary = styled.div`
  margin-top: 3px;
  font-size: 12px;
  color: var(--spotterInkSoft);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TargetLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--spotterAccent);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const ModePill = styled.span`
  font-family: ui-monospace, Menlo, monospace;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 5px;
  letter-spacing: 0.04em;
  color: var(--spotterMerge);
  background: var(--spotterMergeSoft);
  border: 1px solid var(--spotterMergeLine);
  white-space: nowrap;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StatusLabel = styled.span<{ $tone: StageTone }>`
  font-size: 12.5px;
  font-weight: 600;
  color: ${(p) => TONE_COLORS[p.$tone].fg};
`;

/** The four-state Films Ground/Top Films review badge (Awaiting start / Analyzing / Ready to
 *  confirm / Submitted) — restored verbatim (same per-status colors) from the deleted
 *  `AnchorJobPicker.tsx`'s own `StatusBadge` (round-4 review finding: this information was lost,
 *  not relocated, when that component was deleted). Rendered in place of the generic
 *  `StatusLabel` pill for any row with an open review (`RailRowContent` below) — a job's overall
 *  pipeline stage (`stageDisplay`) and its separate anchor-review status are two different axes,
 *  and the generic pill collapsing all three non-submitted review states into one "Needs your
 *  input" label was exactly the ambiguity this restores: an `analyzing` job (system busy, nothing
 *  to do) and a `needs_confirmation` job (a real, clickable action) must not look identical. */
const ReviewStatusBadge = styled.span<{ $status: AnchorJobStatus }>`
  flex: none;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 6px;
  white-space: nowrap;
  ${({ $status }) => {
    switch ($status) {
      case "needs_confirmation":
        return "background: var(--spotterAccentSoft); color: var(--spotterAccent);";
      case "analyzing":
        return "background: var(--spotterFlagSoft); color: var(--spotterFlag);";
      case "submitted":
        return "background: var(--spotterOkSoft); color: var(--spotterOk);";
      case "awaiting_start":
      default:
        return "background: var(--spotterSurface); color: var(--spotterInkFaint); border: 1px solid var(--spotterLine);";
    }
  }}
`;

const StatusNote = styled.div`
  color: var(--spotterInkFaint);
  font-size: 11.5px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Dots = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Dot = styled.i<{ $filled: boolean; $tone: StageTone }>`
  width: 14px;
  height: 4px;
  border-radius: 2px;
  background: ${(p) =>
    p.$filled ? TONE_COLORS[p.$tone].fg : "var(--spotterLine2)"};
`;

const When = styled.span`
  color: var(--spotterInkFaint);
  font-family: ui-monospace, Menlo, monospace;
  font-size: 11px;
`;

const ReviewPaneWrap = styled.div`
  min-width: 0;
`;

/** No \`title\` prop passed anywhere this renders — \`@cinesys/ui\`'s \`Section\` checks
 *  \`title !== undefined\`, not truthiness, so the old \`title=""\` here rendered a genuinely empty
 *  title bar (min-height 45px, its own \`border-bottom\`) above the card's content: that bar, not a
 *  layout bug, was the stray divider line across the card's upper portion. */
const NothingSelectedCard = styled(Section)`
  text-align: center;
  max-width: 480px;
  margin: 60px auto;
`;

/** The bullseye roundel for the friendly "nothing selected" state only — same soft-accent circle
 *  \`AnchorReviewPanel.tsx\`'s \`StatusIcon\` already uses for a status card in this feature, sized
 *  for \`SpotterMark\`. The load-error sibling below is rendered without this — it needs to keep
 *  reading as an error. */
const NothingSelectedIcon = styled.div`
  width: 56px;
  height: 56px;
  margin: 0 auto 16px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: var(--spotterAccentSoft);
`;

const NothingSelectedBody = styled.p`
  font-size: 13.5px;
  color: var(--spotterInkSoft);
  margin: 0;
  line-height: 1.55;
`;

type StageTone = "progress" | "ok" | "warn" | "danger" | "idle";

const TONE_COLORS: Record<StageTone, { fg: string }> = {
  progress: { fg: "var(--spotterMerge)" },
  ok: { fg: "var(--spotterOk)" },
  warn: { fg: "var(--spotterAccent)" },
  danger: { fg: "var(--spotterDanger)" },
  idle: { fg: "var(--spotterInkFaint)" },
};

const MAIN_STAGES: SpotterJobMainStage[] = [
  "queued",
  "processing_footage",
  "identifying_plays",
  "tagging",
  "done",
];

export interface StageDisplay {
  label: string;
  tone: StageTone;
  icon: React.ReactNode | null;
  /** How many of the 5 stepper dots are filled — independent of which label/tone won above, so a
   *  job stuck on `needs_input` still shows real progress. */
  filledDots: number;
}

export function stageDisplay(job: ControlRoomJob): StageDisplay {
  const filledDots = job.latestStage
    ? MAIN_STAGES.indexOf(job.latestStage) + 1
    : 0;

  const state = primaryJobState(job);
  if (state === "done") {
    return {
      label: SPOTTER_STRINGS.controlRoom.stageLabels.done,
      tone: "ok",
      icon: <CheckCircle size={14} />,
      filledDots,
    };
  }
  if (state === "error") {
    return {
      label: SPOTTER_STRINGS.controlRoom.errorLabel,
      tone: "danger",
      icon: <AlertTriangle size={14} />,
      filledDots,
    };
  }
  if (state === "needsReview") {
    return {
      label: SPOTTER_STRINGS.controlRoom.needsInputLabel,
      tone: "warn",
      icon: <HelpCircle size={14} />,
      filledDots,
    };
  }
  // A plain `needsInput` latch with no review (Monday 12937673087, Addison: "don't show needs
  // input when there's nothing to open") falls through to the ordinary stage/idle rendering below
  // instead of its own pill — there's no detail view for it to open (see `ControlRoomJob.hasReview`'s
  // doc comment), so advertising it as actionable would be misleading. `tabForJob` is untouched:
  // this only changes what the row's own pill says, not which rail section it lands in.
  if (job.latestStage) {
    return {
      label: SPOTTER_STRINGS.controlRoom.stageLabels[job.latestStage],
      tone: "progress",
      icon: null,
      filledDots,
    };
  }
  return {
    label: SPOTTER_STRINGS.controlRoom.notYetQueued,
    tone: "idle",
    icon: null,
    filledDots,
  };
}

/** "just now" / "3m ago" / "2h ago" / "5d ago" — the rail row's own last-updated line
 *  (`RailRowContent`), same format the old table's Started/Updated columns used. */
function formatRelativeTime(iso: string): string {
  const diffSec = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (diffSec < 45) {
    return SPOTTER_STRINGS.controlRoom.justNow;
  }
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) {
    return `${diffMin}m ago`;
  }
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) {
    return `${diffHr}h ago`;
  }
  return `${Math.round(diffHr / 24)}d ago`;
}

/** Every job's count under each rail section (`tabForJob`) plus the total — takes the FULL,
 *  unfiltered job list (`hidden` is an ordinary bucket `tabForJob` assigns, not a separate toggle
 *  layered on top, so one pass over every job is enough to get every count right). `all` isn't
 *  rendered as its own rail section (design A4 has no "everything" section) but is kept in the
 *  return shape — used by `ControlRoomScreen`'s own `allJobs.length` checks elsewhere and pinned
 *  by this file's own unit tests. */
export function countJobsByTab(
  jobs: ControlRoomJob[]
): Record<ControlRoomTabKey, number> {
  const counts: Record<ControlRoomTabKey, number> = {
    all: 0,
    needsYou: 0,
    processing: 0,
    done: 0,
    hidden: 0,
  };
  for (const job of jobs) {
    const bucket = tabForJob(job);
    counts[bucket] += 1;
    if (bucket !== "hidden") {
      counts.all += 1;
    }
  }
  return counts;
}

/** The shape `useControlRoomJobs`/`useAnchorJobs` share enough of for the pure render-state
 *  functions below — a structural subset, not the full `UseQueryResult`, so a plain object
 *  literal is enough to unit test them without mocking react-query. */
interface JobsQueryLoadState {
  isLoading: boolean;
  isError: boolean;
}

export type PageBodyState = "pageSpinner" | "emptyNoJobs" | "layout";

/**
 * What `ControlRoomScreen`'s main return renders, given the org-wide list query's load state, the
 * selected `:jobId` (if any), and how many jobs the list actually has. Extracted into its own
 * pure function (this codebase's own convention for `tabForJob`/`stageDisplay`/`countJobsByTab`)
 * because this exact decision has already shipped two real bugs across rounds 2 and 3 of this
 * item — a deep-linked job either waiting on an unrelated list query, or reading as "no jobs yet"
 * while it was actually just still loading. Pinning it with tests (round-4 review finding) means
 * a THIRD regression here fails a unit test instead of needing a live click-through to catch.
 *
 * - `"pageSpinner"` — genuinely nothing to show yet: no job selected, and the list hasn't
 *   resolved. A deep-linked `:jobId` must NEVER wait on this org-wide list query just to start
 *   rendering (round-2 fix) — `jobId` being set skips this unconditionally.
 * - `"emptyNoJobs"` — the list resolved with zero jobs, AND there's no selected job to render
 *   instead. `jobId` being set skips this too (round-3 fix): a cold deep link with no cached list
 *   data yet otherwise reads as "no jobs yet" for a job link that's actually valid.
 * - `"layout"` — the normal two-column rail + review-pane render. This is also where the list
 *   query having genuinely failed (`isError`) falls through to, whether or not a job is selected —
 *   `RailPane` shows its own error there (`railPaneState` below), independent of this decision.
 */
export function pageBodyState(
  jobsQuery: JobsQueryLoadState,
  jobId: string | undefined,
  jobCount: number
): PageBodyState {
  if (jobsQuery.isLoading && !jobId) {
    return "pageSpinner";
  }
  if (jobCount === 0 && !jobsQuery.isError && !jobId) {
    return "emptyNoJobs";
  }
  return "layout";
}

export type RailPaneState = "error" | "loading" | "sections";

/**
 * What `RailPane` itself renders — deliberately a function of the list query ALONE, with no
 * `jobId` parameter at all. That absence is load-bearing, not an oversight: it's the structural
 * guarantee (round-2 review finding) that the rail's own loading/error state can never reach into
 * `ReviewPaneWrap` and unmount `AnchorReviewPanel` (and the unsaved anchor-confirmation draft
 * state it owns) out from under a customer mid-review just because this unrelated, 5s-polled list
 * query hit a transient blip.
 */
export function railPaneState(jobsQuery: JobsQueryLoadState): RailPaneState {
  if (jobsQuery.isError) {
    return "error";
  }
  if (jobsQuery.isLoading) {
    return "loading";
  }
  return "sections";
}

/** Point 12 — the rail's small inline refresh spinner shows for either query's background
 *  refetch, not just `jobsQuery`'s (both drive what a rail row shows — see `RailRowContent`'s doc
 *  comment). `isFetching`, not `isLoading`: `jobsQuery`'s own FIRST load never reaches this call
 *  site at all (`pageBodyState` shows `"pageSpinner"` instead), so there's no separate "first
 *  load" case to exclude here the way `railPaneState` has to for its own loading state. */
export function showRefreshSpinner(
  jobsQuery: { isFetching: boolean },
  anchorJobsQuery: { isFetching: boolean }
): boolean {
  return jobsQuery.isFetching || anchorJobsQuery.isFetching;
}

export type NothingSelectedState = "loadError" | "waiting";

/** What the right pane's "nothing selected" card says when no job is selected — `"loadError"`
 *  when the list itself failed to load (round-4 review finding: the default "nothing needs your
 *  attention" copy read as "the list is empty" right next to the rail's own error banner during a
 *  real failure, which is actively misleading, not just unhelpful). */
export function nothingSelectedState(jobsQuery: {
  isError: boolean;
}): NothingSelectedState {
  return jobsQuery.isError ? "loadError" : "waiting";
}

/** Which review-status badge (if any) a rail row shows (`ReviewStatusBadge`) — `undefined` for a
 *  job with no review to open, OR one whose status hasn't loaded yet from `useAnchorJobs()` (a
 *  separate, independent query from the org-wide list — see `RailRowContent`'s doc comment); that
 *  row just falls back to the generic `stageDisplay` pill until it does. */
export function reviewBadgeStatus(
  job: ControlRoomJob,
  statusByJobId: Map<string, AnchorJobStatus>
): AnchorJobStatus | undefined {
  return job.hasReview ? statusByJobId.get(job.jobId) : undefined;
}

type RailSectionKey = Exclude<ControlRoomTabKey, "all">;

/** Rail section order (design A4) — Needs You / In Progress / Done / Hidden, always in this
 *  order regardless of counts. */
const RAIL_SECTION_ORDER: RailSectionKey[] = [
  "needsYou",
  "processing",
  "done",
  "hidden",
];

/** Needs You starts expanded, the other three start folded — see the file doc comment's "RAIL
 *  SECTIONS AND BUCKETING" section for why. `true` means folded/collapsed. */
const DEFAULT_RAIL_FOLD: Record<RailSectionKey, boolean> = {
  needsYou: false,
  processing: true,
  done: true,
  hidden: true,
};

const RAIL_FOLD_STORAGE_KEY = "spotter-control-room-rail-fold";

function isRailSectionKey(value: string | null): value is RailSectionKey {
  return !!value && (RAIL_SECTION_ORDER as string[]).includes(value);
}

function loadRailFold(): Record<RailSectionKey, boolean> {
  try {
    const raw = window.localStorage.getItem(RAIL_FOLD_STORAGE_KEY);
    if (!raw) {
      return DEFAULT_RAIL_FOLD;
    }
    const parsed = JSON.parse(raw) as Partial<Record<RailSectionKey, unknown>>;
    const next = { ...DEFAULT_RAIL_FOLD };
    for (const key of RAIL_SECTION_ORDER) {
      if (typeof parsed[key] === "boolean") {
        next[key] = parsed[key] as boolean;
      }
    }
    return next;
  } catch {
    // Corrupt/blocked storage (private browsing, a stale shape from an earlier version) — fall
    // back to the default rather than throw.
    return DEFAULT_RAIL_FOLD;
  }
}

function saveRailFold(state: Record<RailSectionKey, boolean>): void {
  try {
    window.localStorage.setItem(RAIL_FOLD_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Best-effort — a full/blocked store just means fold state doesn't survive reload, not a
    // broken page.
  }
}

/** The rail row's primary label + subline (Monday 12937644849) — `resolveRowLabel` (`rowLabel.ts`)
 *  picks the resolved target name, the game-mode label, or the job id itself; this just renders
 *  whatever it returns. The raw job id is always a tooltip away for support even when it isn't
 *  the primary text: it's on `RowPrimary`'s own `title` for every row, but a `collection`/
 *  `saved_search` row wraps its text in `TargetLink`, whose nested `title` would otherwise
 *  shadow the outer one — so that case folds the job id into the link's own title instead. */
function JobLabelCell(props: { job: ControlRoomJob }) {
  const { job } = props;
  const label = resolveRowLabel(job);
  return (
    <JobCell>
      <RowPrimary $mono={label.isJobId} title={job.jobId}>
        {label.url ? (
          <TargetLink
            href={label.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title={SPOTTER_STRINGS.controlRoom.openInIconikTitle(job.jobId)}
          >
            {label.primary}
            <ExternalLink size={11} />
          </TargetLink>
        ) : (
          label.primary
        )}
      </RowPrimary>
      {label.secondary && <RowSecondary>{label.secondary}</RowSecondary>}
    </JobCell>
  );
}

/** One rail row — label, mode pill (with the "unrecognized mode" fallback below), a status pill
 *  (the generic pipeline-stage pill, OR `ReviewStatusBadge` for a row with an open review — see
 *  that component's doc comment), the 5-dot stepper, and the hide/unhide control. Only wired up
 *  as clickable/navigable when the job is actually reviewable (`job.hasReview`, same gate the old
 *  table used for row clickability) — a plain pipeline job with no anchor review has nothing for
 *  `AnchorReviewPanel` to show.
 *
 *  `reviewStatus` is looked up by the caller (`renderRailSections`) from `useAnchorJobs()` — a
 *  SEPARATE, independent query from the org-wide `useControlRoomJobs()` list this row's other
 *  fields come from (same "every Films Ground job for the org" list the old `AnchorJobPicker.tsx`
 *  itself read, per that hook's own doc comment) — `ControlRoomJob` has no per-job `AnchorJobStatus`
 *  of its own, only the coarser `reviewNeedsInput` boolean.
 *
 *  CLICKABLE = a real `Link` (round-4 review finding: keyboard-only users had no way to open a
 *  job at all — see `RailRow`'s own doc comment for why this is a `Link`, not a hand-rolled
 *  `role="button"`). The hide/unhide `Button` nested inside both stops propagation AND prevents
 *  the click's default action — needed now that the row is a real `<a>` under the hood: without
 *  `preventDefault`, stopping propagation alone still lets the browser follow the row's own
 *  `href` natively (a full-page navigation, not React Router's), even though it stops the row's
 *  React `onClick` from ever firing. */
function RailRowContent(props: {
  job: ControlRoomJob;
  active: boolean;
  reviewStatus: AnchorJobStatus | undefined;
  onSelected: () => void;
  onToggleDismissed: (job: ControlRoomJob) => void;
  pending: boolean;
}) {
  const { job, active, reviewStatus, onSelected, onToggleDismissed, pending } =
    props;
  const stage = stageDisplay(job);
  const clickable = job.hasReview;
  const buttonMode = hideButtonMode(job);

  const content = (
    <>
      <JobLabelCell job={job} />
      <RailRowMeta>
        <ModePill>
          {/* Bare object index with no fallback used to render a BLANK pill for any mode not
              yet in `modeLabels` (`SpotterJobMode`'s TS type is only a compile-time promise — a
              backend/frontend drift, or a genuinely new mode the two apps haven't been kept in
              sync on yet, still arrives as real JSON at runtime). Falls back to the existing
              "Unknown" label instead of rendering nothing. */}
          {SPOTTER_STRINGS.controlRoom.modeLabels[job.mode] ??
            SPOTTER_STRINGS.controlRoom.modeLabels.unknown}
        </ModePill>
        {reviewStatus ? (
          <ReviewStatusBadge $status={reviewStatus}>
            {SPOTTER_STRINGS.filmsGround.jobStatusLabel[reviewStatus]}
          </ReviewStatusBadge>
        ) : (
          <StatusRow>
            {stage.icon}
            <StatusLabel $tone={stage.tone}>{stage.label}</StatusLabel>
          </StatusRow>
        )}
      </RailRowMeta>
      {job.note && <StatusNote title={job.note}>{job.note}</StatusNote>}
      <RailRowMeta>
        <Dots>
          {MAIN_STAGES.map((s, i) => (
            <Dot
              key={s}
              $filled={i < stage.filledDots}
              $tone={stage.tone === "idle" ? "idle" : stage.tone}
            />
          ))}
        </Dots>
        <When>{formatRelativeTime(job.lastUpdatedAt)}</When>
      </RailRowMeta>
      <RailRowAction>
        <Button
          icon={buttonMode === "unhide" ? RotateCcw : EyeOff}
          borderless
          type={buttonMode === "unhide" ? "default" : "danger"}
          loading={pending}
          tooltip={
            buttonMode === "unhide"
              ? SPOTTER_STRINGS.controlRoom.unhideTooltip
              : SPOTTER_STRINGS.controlRoom.hideJobTooltip
          }
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggleDismissed(job);
          }}
        />
      </RailRowAction>
    </>
  );

  if (!clickable) {
    return (
      <RailRow $active={active} $clickable={false}>
        {content}
      </RailRow>
    );
  }

  return (
    <RailRow
      as={Link}
      to={`/spotter/control-room/${job.jobId}`}
      $active={active}
      $clickable
      onClick={onSelected}
    >
      {content}
    </RailRow>
  );
}

/** One rail section — fold/expand animated (Point 4) via `@cinesys/ui`'s `AnimateHeight`, its
 *  first consumer in this app (verified present at the pinned `@cinesys/ui@0.0.30`). Its own
 *  component (not inline JSX in `renderRailSections` below) so it can own the `AnimateHeightRef`
 *  and call `.update()` at the right moment: BEFORE the fold state flips, in the same handler —
 *  `AnimateHeight` measures its old/new rendered height across that one synchronous update and
 *  animates between them (see `InputGroup`'s own use of it in `@cinesys/ui`, the only existing
 *  reference for this contract). */
function RailSection(props: {
  sectionKey: RailSectionKey;
  jobs: ControlRoomJob[];
  expanded: boolean;
  onToggleFold: (key: RailSectionKey) => void;
  selectedJobId: string | undefined;
  reviewStatusByJobId: Map<string, AnchorJobStatus>;
  onRowSelected: () => void;
  onToggleDismissed: (job: ControlRoomJob) => void;
  pendingJobIds: Set<string>;
}): React.ReactElement {
  const {
    sectionKey,
    jobs,
    expanded,
    onToggleFold,
    selectedJobId,
    reviewStatusByJobId,
    onRowSelected,
    onToggleDismissed,
    pendingJobIds,
  } = props;
  const labels = SPOTTER_STRINGS.controlRoom.railSectionLabels;
  const animateRef = useRef<AnimateHeightRef>(null);

  const handleToggle = () => {
    animateRef.current?.update();
    onToggleFold(sectionKey);
  };

  return (
    <RailSectionBlock>
      <RailSectionHeaderBtn
        type="button"
        aria-expanded={expanded}
        onClick={handleToggle}
        title={
          expanded
            ? SPOTTER_STRINGS.controlRoom.collapseSectionTitle
            : SPOTTER_STRINGS.controlRoom.expandSectionTitle
        }
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        {labels[sectionKey]}
        <RailSectionCount>{jobs.length}</RailSectionCount>
      </RailSectionHeaderBtn>
      <AnimateHeight ref={animateRef}>
        {expanded && jobs.length > 0 && (
          <RailSectionRows>
            {jobs.map((job) => (
              <RailRowContent
                key={job.jobId}
                job={job}
                active={job.jobId === selectedJobId}
                reviewStatus={reviewBadgeStatus(job, reviewStatusByJobId)}
                onSelected={onRowSelected}
                onToggleDismissed={onToggleDismissed}
                pending={pendingJobIds.has(job.jobId)}
              />
            ))}
          </RailSectionRows>
        )}
      </AnimateHeight>
    </RailSectionBlock>
  );
}

/** The four rail sections, in order — a plain function (not a shared component instance) so
 *  `ControlRoomScreen` can call it twice (the always-mounted desktop rail, and again inside the
 *  mobile drawer) without the two trees fighting over one DOM node. Each `RailSection` above owns
 *  its own fold-animation state, so the two calls don't share that either. */
function renderRailSections(props: {
  jobs: ControlRoomJob[];
  fold: Record<RailSectionKey, boolean>;
  onToggleFold: (key: RailSectionKey) => void;
  selectedJobId: string | undefined;
  reviewStatusByJobId: Map<string, AnchorJobStatus>;
  onRowSelected: () => void;
  onToggleDismissed: (job: ControlRoomJob) => void;
  pendingJobIds: Set<string>;
}): React.ReactNode {
  const { jobs, fold, ...rest } = props;

  return RAIL_SECTION_ORDER.map((key) => {
    const sectionJobs = jobs.filter((j) => tabForJob(j) === key);
    return (
      <RailSection
        key={key}
        sectionKey={key}
        jobs={sectionJobs}
        expanded={!fold[key]}
        {...rest}
      />
    );
  });
}

function NewRunControls(props: { onClick: () => void; fetching: boolean }) {
  return (
    <NewRunRow>
      <NewRunButton
        icon={Plus}
        label={SPOTTER_STRINGS.controlRoom.newRunButton}
        type="default"
        onClick={props.onClick}
      />
      <RefreshSlot>{props.fetching && <Spinner size={16} />}</RefreshSlot>
    </NewRunRow>
  );
}

/** The desktop rail's own "+ New run" — see `RailHeader`'s doc comment. Button left-aligned,
 *  spinner right-aligned (Addison: "left align the button and right align the loading symbol") and
 *  NOT full width (plain `Button`, no `flex: 1` wrapper) — `RailHeader`'s `justify-content:
 *  space-between` does the aligning given just those two children in this order. */
function NewRunHeader(props: { onClick: () => void; fetching: boolean }) {
  return (
    <RailHeader>
      <Button
        icon={Plus}
        label={SPOTTER_STRINGS.controlRoom.newRunButton}
        type="default"
        onClick={props.onClick}
      />
      <RefreshSlot $size={HEADER_SPINNER_SIZE}>
        {props.fetching && <Spinner size={HEADER_SPINNER_SIZE} />}
      </RefreshSlot>
    </RailHeader>
  );
}

const ControlRoomScreen = () => {
  const access = useInterfaceAccess(SPOTTER_INTERFACE_ID);
  const jobsQuery = useControlRoomJobs();
  // A separate, independent query from `jobsQuery` above (see `RailRowContent`'s doc comment) —
  // its only use on this screen is resolving each review-bearing row's specific
  // `AnchorJobStatus` badge (`reviewStatusByJobId` below); its own load/error state deliberately
  // has no effect on anything else here (a missed badge just falls back to the generic pill).
  const anchorJobsQuery = useAnchorJobs();
  const dismissMutation = useSetControlRoomJobDismissed();
  // Present -> show that job's Anchor Review panel in place of the "nothing selected" state (see
  // file doc comment's "ONE-PAGE RAIL" section). This is the SAME `:jobId` param `AnchorReviewPanel`
  // itself reads via its own props — this screen owns the route param, not the panel.
  const { jobId } = useParams<{ jobId: string }>();

  // `?tab=<section>` is the old chip-selection URL shape — no longer a filter (the rail shows
  // every section at once, just folded), but a link carrying it should still DO something rather
  // than silently no-op: it expands that section on load. Read once (initializer only), not kept
  // in sync afterward — see the `foldState` initializer just below.
  const [searchParams] = useSearchParams();
  const inboundTab = searchParams.get("tab");

  const [foldState, setFoldState] = useState<Record<RailSectionKey, boolean>>(
    () => {
      const stored = loadRailFold();
      return isRailSectionKey(inboundTab)
        ? { ...stored, [inboundTab]: false }
        : stored;
    }
  );

  const toggleFold = (key: RailSectionKey) => {
    setFoldState((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      saveRailFold(next);
      return next;
    });
  };

  const [railDrawerOpen, setRailDrawerOpen] = useState(false);
  useEffect(() => {
    if (!railDrawerOpen) {
      return undefined;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setRailDrawerOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [railDrawerOpen]);

  // Auto-closes the drawer if the viewport widens past `RAIL_BREAKPOINT` while it's open (a
  // window resize, orientation change, or DevTools device-mode toggle) — otherwise the drawer's
  // full-screen overlay would sit on top of `RailPane`, which becomes visible again at that width,
  // showing two clickable copies of the same rail at once (code-review finding, round 2).
  useEffect(() => {
    if (!railDrawerOpen) {
      return undefined;
    }
    const mql = window.matchMedia(`(min-width: ${RAIL_BREAKPOINT_MIN_PX}px)`);
    const onChange = (e: MediaQueryListEvent) => {
      if (e.matches) {
        setRailDrawerOpen(false);
      }
    };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [railDrawerOpen]);

  // jobIds whose hide/unhide mutation is currently in flight — tracked locally, NOT via
  // `dismissMutation.isLoading`/`.variables` (a single shared mutation observer reflects only the
  // LATEST `.mutate()` call), so two rows hidden in quick succession each get their own correct
  // spinner instead of racing each other for it. Declared here, alongside this component's other
  // hooks and ABOVE every conditional early return below — rules-of-hooks.
  const [pendingJobIds, setPendingJobIds] = React.useState<Set<string>>(
    new Set()
  );

  // "Start a new run" (Monday 12921140208) — `showNewRun` gates the modal; `justStarted` drives a
  // transient success banner on THIS screen (the modal itself is already closed by the time it's
  // set). No jobId comes back to link to (see `NewRunModal.tsx`'s doc comment), so this can only
  // ever say "it's coming", not point at the row directly — cleared on a timeout rather than left
  // to accumulate across multiple runs started in one sitting.
  const [showNewRun, setShowNewRun] = useState(false);
  const [justStarted, setJustStarted] = useState(false);

  // Org-level entitlement gate — see FieldsScreen.tsx's equivalent doc comment.
  if (access.status !== "granted") {
    return (
      <Page>
        <SpotterHeader />
        {access.status === "denied" && (
          <Wrap>
            <InterfaceAccessNotice reason={access.reason} />
          </Wrap>
        )}
        {access.status === "pending" && <DelayedSpinner padTop />}
      </Page>
    );
  }

  // Blocks the WHOLE page only when there's genuinely nothing else to show yet (no job selected,
  // list still loading) — a deep-linked/bookmarked `:jobId` (the "resume this job" flow this
  // screen's own doc comment calls out as still supported) must never wait on the ORG-WIDE list
  // query before it can even start fetching that one job. See the "REVIEW PANE INDEPENDENT OF THE
  // LIST QUERY" section below for why the same reasoning extends past this initial load too.
  const allJobs = jobsQuery.data ?? [];
  const tabCounts = countJobsByTab(allJobs);
  const reviewStatusByJobId = new Map(
    (anchorJobsQuery.data ?? []).map((j) => [j.jobId, j.status] as const)
  );
  const bodyState = pageBodyState(jobsQuery, jobId, allJobs.length);
  const isRefreshing = showRefreshSpinner(jobsQuery, anchorJobsQuery);

  if (bodyState === "pageSpinner") {
    return (
      <Page>
        <SpotterHeader />
        <Spinner padTop />
      </Page>
    );
  }

  const toggleDismissed = (job: ControlRoomJob) => {
    const id = job.jobId;
    // Off the row's actual bucket, not `job.dismissed` — a stale-only row's `dismissed` is
    // `false` even though it's showing under Hidden, so `!job.dismissed` would send `true`
    // (re-hide) instead of the `false` (explicit unhide) the visible Unhide button promised.
    const isHidden = tabForJob(job) === "hidden";
    setPendingJobIds((prev) => new Set(prev).add(id));
    dismissMutation.mutate(
      { jobId: id, dismissed: !isHidden },
      {
        onSettled: () => {
          setPendingJobIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        },
      }
    );
  };

  // Rail rows navigate themselves now (`RailRow`'s `as={Link}` — see its doc comment); the only
  // thing this screen still needs to do on a row click is close the mobile drawer, if open.
  const onRowSelected = () => {
    setRailDrawerOpen(false);
  };

  return (
    <Page>
      <SpotterHeader />
      <Wrap>
        {justStarted && (
          <Banner
            success={SPOTTER_STRINGS.controlRoom.newRunStarted}
            padBottom
          />
        )}

        {bodyState === "emptyNoJobs" ? (
          // See `pageBodyState`'s own doc comment for the full "why" — this used to be an inline
          // condition (`allJobs.length === 0 && !jobsQuery.isError && !jobId`) that shipped two
          // real regressions across rounds 2 and 3 of this item; it's a pure, unit-tested function
          // now specifically so a third one fails a test instead of needing a live click-through.
          // `Layout`/`RailPane` don't mount in this state, so `NewRunControls` renders standalone
          // here — a brand-new org with zero jobs ever still needs a way to start its first run.
          <>
            <NewRunControls
              onClick={() => setShowNewRun(true)}
              fetching={isRefreshing}
            />
            <EmptyPanel>{SPOTTER_STRINGS.controlRoom.empty}</EmptyPanel>
          </>
        ) : (
          <Layout>
            {/*
             * REVIEW PANE INDEPENDENT OF THE LIST QUERY (code-review finding, round 2): the rail
             * (left) is this org-wide list query's own UI — its loading/error states stay scoped
             * to `RailPane` alone, below. `AnchorReviewPanel` (right) runs its own independent
             * `useAnchorJob` fetch and owns real unsaved-draft state (`AnchorConfirmContent`'s
             * `resolutions`/`openAnchorIds`) — it must never unmount because THIS 5s-polled list
             * query hit a transient error or is mid-refetch, or a customer mid-way through
             * confirming anchors would silently lose that work the next time the poll blips.
             * Previously (pre-rail) the jobId route was an early return entirely above any
             * `jobsQuery` check, which had the same effect by construction; folding the review
             * pane into this shared two-column layout must not lose that independence.
             */}
            <RailColumn>
              <NewRunHeader
                onClick={() => setShowNewRun(true)}
                fetching={isRefreshing}
              />
              <RailPane aria-label={SPOTTER_STRINGS.controlRoom.jobsListLabel}>
                {railPaneState(jobsQuery) === "error" ? (
                  <Banner
                    error={
                      isSpotterAccessDeniedError(jobsQuery.error)
                        ? SPOTTER_STRINGS.controlRoom.accessDeniedError
                        : SPOTTER_STRINGS.controlRoom.loadError
                    }
                    padTop
                  />
                ) : railPaneState(jobsQuery) === "loading" ? (
                  <Spinner padTop />
                ) : (
                  renderRailSections({
                    jobs: allJobs,
                    fold: foldState,
                    onToggleFold: toggleFold,
                    selectedJobId: jobId,
                    reviewStatusByJobId,
                    onRowSelected,
                    onToggleDismissed: toggleDismissed,
                    pendingJobIds,
                  })
                )}
              </RailPane>
            </RailColumn>

            <ReviewPaneWrap>
              <RailDrawerTriggerWrap>
                {/* `RailPane` itself is `display: none` below `RAIL_BREAKPOINT` (see its own
                    styles), so its `NewRunControls` never reaches a mobile viewport — this is the
                    one other place a mobile customer can start a run. */}
                <NewRunControls
                  onClick={() => setShowNewRun(true)}
                  fetching={isRefreshing}
                />
                <Button
                  icon={Menu}
                  label={SPOTTER_STRINGS.controlRoom.jobsListLabel}
                  type="default"
                  onClick={() => setRailDrawerOpen(true)}
                />
              </RailDrawerTriggerWrap>

              {jobId ? (
                <AnchorReviewPanel key={jobId} jobId={jobId} />
              ) : nothingSelectedState(jobsQuery) === "loadError" ? (
                // The list itself failed to load (round-4 review finding) — the default "nothing
                // needs your attention" copy below reads as "the list is empty", which is actively
                // misleading sitting right next to the rail's own error banner during a real
                // failure. Say we couldn't load, not that there's nothing there — no bullseye
                // roundel here, this state must keep reading as an error.
                <NothingSelectedCard pad>
                  <CardHeading>
                    {SPOTTER_STRINGS.controlRoom.nothingSelectedLoadErrorTitle}
                  </CardHeading>
                  <NothingSelectedBody>
                    {SPOTTER_STRINGS.controlRoom.nothingSelectedLoadErrorBody}
                  </NothingSelectedBody>
                </NothingSelectedCard>
              ) : (
                <NothingSelectedCard pad>
                  <NothingSelectedIcon>
                    <SpotterMark size={28} />
                  </NothingSelectedIcon>
                  <CardHeading>
                    {SPOTTER_STRINGS.controlRoom.nothingSelectedTitle}
                  </CardHeading>
                  <NothingSelectedBody>
                    {SPOTTER_STRINGS.controlRoom.nothingSelectedBody(
                      tabCounts.needsYou
                    )}
                  </NothingSelectedBody>
                </NothingSelectedCard>
              )}
            </ReviewPaneWrap>
          </Layout>
        )}
      </Wrap>
      {showNewRun && (
        <NewRunModal
          onClose={() => setShowNewRun(false)}
          onStarted={() => {
            setJustStarted(true);
            window.setTimeout(() => setJustStarted(false), 8_000);
          }}
        />
      )}
      {railDrawerOpen &&
        createPortal(
          <RailDrawerOverlay onClick={() => setRailDrawerOpen(false)}>
            <RailDrawerPanel onClick={(e) => e.stopPropagation()}>
              <RailDrawerHeader>
                <RailDrawerTitle>
                  {SPOTTER_STRINGS.controlRoom.jobsListLabel}
                </RailDrawerTitle>
                <Button
                  icon={X}
                  borderless
                  type="default"
                  onClick={() => setRailDrawerOpen(false)}
                  tooltip={SPOTTER_STRINGS.controlRoom.closeButton}
                />
              </RailDrawerHeader>
              {renderRailSections({
                jobs: allJobs,
                fold: foldState,
                onToggleFold: toggleFold,
                selectedJobId: jobId,
                reviewStatusByJobId,
                onRowSelected,
                onToggleDismissed: toggleDismissed,
                pendingJobIds,
              })}
            </RailDrawerPanel>
          </RailDrawerOverlay>,
          document.body
        )}
    </Page>
  );
};

export default ControlRoomScreen;
